/**
 * This hook is used to inject a hidden iframe into the body to prefetch resources on a future page navigation
 */
import { getDocument } from "@faire/web/common/globals/getDocument";
import { useCallback, useEffect, useRef } from "react";

export const useInjectIframePrefetchCallback = (url: string) => {
  const hasInjectIframePrefetch = useRef(false);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const injectIframe = useCallback(
    (doNotRun?: boolean, trackingFn?: () => void) => {
      if (hasInjectIframePrefetch.current || doNotRun) {
        return;
      }
      trackingFn?.();
      const element = createHiddenIframe(url);
      appendElementToBody(element);
      hasInjectIframePrefetch.current = true;

      cleanupRef.current = () => {
        removeElementFromBody(element);
        hasInjectIframePrefetch.current = false;
      };

      return cleanupRef.current;
    },
    [url]
  );

  return injectIframe;
};

const createHiddenIframe = (url: string): HTMLIFrameElement | undefined => {
  const iframe = getDocument()?.createElement("iframe");
  if (!iframe) {
    return;
  }
  iframe.style.display = "none";
  iframe.src = url;
  iframe.sandbox = "allow-same-origin allow-scripts";
  return iframe;
};

const appendElementToBody = (element: HTMLElement | undefined) => {
  if (!element) {
    return;
  }
  getDocument()?.body.appendChild(element);
};

const removeElementFromBody = (element: HTMLElement | undefined) => {
  if (!element) {
    return;
  }
  getDocument()?.body.removeChild(element);
};
